// ============================================================
// Champions data via Riot Data Dragon
// ============================================================

const DDRAGON_BASE = 'https://ddragon.leagueoflegends.com';
const FALLBACK_VERSION = '15.1.1'; // fallback au cas où l'API est down

// Fallback list si l'API Data Dragon ne répond pas
// (on embarque une liste hardcodée de tous les champions communs pour éviter les layouts cassés)
const FALLBACK_CHAMPIONS = [
  'Aatrox', 'Ahri', 'Akali', 'Akshan', 'Alistar', 'Ambessa', 'Amumu', 'Anivia', 'Annie', 'Aphelios', 'Ashe', 'AurelionSol', 'Aurora', 'Azir',
  'Bard', 'Belveth', 'Blitzcrank', 'Brand', 'Braum', 'Briar',
  'Caitlyn', 'Camille', 'Cassiopeia', 'Chogath', 'Corki',
  'Darius', 'Diana', 'Draven', 'DrMundo',
  'Ekko', 'Elise', 'Evelynn', 'Ezreal',
  'Fiddlesticks', 'Fiora', 'Fizz',
  'Galio', 'Gangplank', 'Garen', 'Gnar', 'Gragas', 'Graves', 'Gwen',
  'Hecarim', 'Heimerdinger', 'Hwei',
  'Illaoi', 'Irelia', 'Ivern',
  'Janna', 'JarvanIV', 'Jax', 'Jayce', 'Jhin', 'Jinx',
  'Kaisa', 'Kalista', 'Karma', 'Karthus', 'Kassadin', 'Katarina', 'Kayle', 'Kayn', 'Kennen', 'Khazix', 'Kindred', 'Kled', 'KogMaw', 'KSante',
  'Leblanc', 'LeeSin', 'Leona', 'Lillia', 'Lissandra', 'Lucian', 'Lulu', 'Lux',
  'Malphite', 'Malzahar', 'Maokai', 'MasterYi', 'Mel', 'Milio', 'MissFortune', 'Mordekaiser', 'Morgana',
  'Naafiri', 'Nami', 'Nasus', 'Nautilus', 'Neeko', 'Nidalee', 'Nilah', 'Nocturne', 'Nunu',
  'Olaf', 'Orianna', 'Ornn',
  'Pantheon', 'Poppy', 'Pyke',
  'Qiyana', 'Quinn',
  'Rakan', 'Rammus', 'RekSai', 'Rell', 'Renata', 'Renekton', 'Rengar', 'Riven', 'Rumble', 'Ryze',
  'Samira', 'Sejuani', 'Senna', 'Seraphine', 'Sett', 'Shaco', 'Shen', 'Shyvana', 'Singed', 'Sion', 'Sivir', 'Skarner', 'Smolder', 'Sona', 'Soraka', 'Swain', 'Sylas', 'Syndra',
  'TahmKench', 'Taliyah', 'Talon', 'Taric', 'Teemo', 'Thresh', 'Tristana', 'Trundle', 'Tryndamere', 'TwistedFate', 'Twitch',
  'Udyr', 'Urgot',
  'Varus', 'Vayne', 'Veigar', 'Velkoz', 'Vex', 'Vi', 'Viego', 'Viktor', 'Vladimir', 'Volibear',
  'Warwick', 'MonkeyKing', // Wukong
  'Xayah', 'Xerath', 'XinZhao',
  'Yasuo', 'Yone', 'Yorick', 'Yuumi',
  'Zac', 'Zed', 'Zeri', 'Ziggs', 'Zilean', 'Zoe', 'Zyra'
];

// Mapping display name -> Data Dragon ID pour les noms qui diffèrent
const DISPLAY_NAME_OVERRIDES = {
  'Kaisa': "Kai'Sa",
  'Khazix': "Kha'Zix",
  'Chogath': "Cho'Gath",
  'KogMaw': "Kog'Maw",
  'Belveth': "Bel'Veth",
  'Velkoz': "Vel'Koz",
  'RekSai': "Rek'Sai",
  'LeeSin': 'Lee Sin',
  'JarvanIV': 'Jarvan IV',
  'MasterYi': 'Master Yi',
  'MissFortune': 'Miss Fortune',
  'MonkeyKing': 'Wukong',
  'TahmKench': 'Tahm Kench',
  'TwistedFate': 'Twisted Fate',
  'XinZhao': 'Xin Zhao',
  'AurelionSol': 'Aurelion Sol',
  'DrMundo': 'Dr. Mundo',
  'KSante': "K'Sante",
  'Nunu': 'Nunu & Willump'
};

let cachedVersion = null;
let cachedChampions = null;

export async function getDataDragonVersion() {
  if (cachedVersion) return cachedVersion;
  try {
    const res = await fetch(`${DDRAGON_BASE}/api/versions.json`);
    const versions = await res.json();
    cachedVersion = versions[0];
    return cachedVersion;
  } catch (e) {
    console.warn('Fallback Data Dragon version:', e);
    cachedVersion = FALLBACK_VERSION;
    return FALLBACK_VERSION;
  }
}

export async function getAllChampions() {
  if (cachedChampions) return cachedChampions;
  try {
    const version = await getDataDragonVersion();
    const res = await fetch(`${DDRAGON_BASE}/cdn/${version}/data/en_US/champion.json`);
    const data = await res.json();
    // data.data is an object { Aatrox: {...}, Ahri: {...} }
    cachedChampions = Object.values(data.data).map(c => ({
      id: c.id,
      name: c.name,
      title: c.title
    })).sort((a, b) => a.name.localeCompare(b.name));
    return cachedChampions;
  } catch (e) {
    console.warn('Fallback champions list:', e);
    cachedChampions = FALLBACK_CHAMPIONS.map(id => ({
      id,
      name: DISPLAY_NAME_OVERRIDES[id] || id,
      title: ''
    })).sort((a, b) => a.name.localeCompare(b.name));
    return cachedChampions;
  }
}

/**
 * Mapping inverse : display name (avec apostrophes/espaces) → ID DataDragon (sans).
 * Construit à partir de DISPLAY_NAME_OVERRIDES.
 */
const ID_FROM_DISPLAY = Object.entries(DISPLAY_NAME_OVERRIDES).reduce((acc, [id, display]) => {
  acc[display.toLowerCase()] = id;
  return acc;
}, {});

/**
 * Résout un nom de champion (display ou ID) vers son ID DataDragon canonique.
 * Ex : "Kai'Sa" → "Kaisa", "Lee Sin" → "LeeSin", "Lucian" → "Lucian"
 */
function resolveChampionId(input) {
  if (!input) return input;
  // Cas 1 : c'est déjà un ID DDragon connu (présent dans la liste fallback ou cache)
  if ((cachedChampions || FALLBACK_CHAMPIONS.map(id => ({ id }))).some(c => c.id === input)) {
    return input;
  }
  // Cas 2 : c'est un display name connu (lookup inverse)
  const fromDisplay = ID_FROM_DISPLAY[input.toLowerCase()];
  if (fromDisplay) return fromDisplay;
  // Cas 3 : on tente de nettoyer les apostrophes / espaces / accents
  return input.replace(/['\s’]/g, '');
}

export function getChampionIconUrl(championId, version) {
  const v = version || cachedVersion || FALLBACK_VERSION;
  const id = resolveChampionId(championId);
  return `${DDRAGON_BASE}/cdn/${v}/img/champion/${id}.png`;
}

/**
 * URL DataDragon pour le splash art d'un champion (1215×717).
 * Utilisé en background décoratif (opacity faible).
 */
export function getChampionSplashUrl(championId, skin = 0) {
  const id = resolveChampionId(championId);
  return `${DDRAGON_BASE}/cdn/img/champion/splash/${id}_${skin}.jpg`;
}

/**
 * URL DataDragon pour l'icône d'un item.
 * @param {number|string} itemId - ID Riot de l'item
 * @param {string} version - version DDragon (optionnel)
 */
export function getItemIconUrl(itemId, version) {
  if (!itemId || itemId === 0) return null;
  const v = version || cachedVersion || FALLBACK_VERSION;
  return `${DDRAGON_BASE}/cdn/${v}/img/item/${itemId}.png`;
}

let cachedRunes = null; // Map { perkId: iconPath } depuis runesReforged.json
let cachedBootsIds = null; // Set<number> des IDs d'items qui sont des bottes

/**
 * Charge runesReforged.json et construit un cache id → iconPath.
 * iconPath est relatif à `cdn/img/` (ex: 'perk-images/Styles/Precision/LethalTempo/LethalTempoTemp.png')
 */
export async function getAllRunes() {
  if (cachedRunes) return cachedRunes;
  try {
    const version = await getDataDragonVersion();
    const res = await fetch(`${DDRAGON_BASE}/cdn/${version}/data/en_US/runesReforged.json`);
    const styles = await res.json();
    const map = {};
    styles.forEach(style => {
      // Style lui-même (ex: 8000 Precision)
      if (style.id && style.icon) map[style.id] = style.icon;
      // Slots de runes
      if (Array.isArray(style.slots)) {
        style.slots.forEach(slot => {
          if (Array.isArray(slot.runes)) {
            slot.runes.forEach(rune => {
              if (rune.id && rune.icon) map[rune.id] = rune.icon;
            });
          }
        });
      }
    });
    cachedRunes = map;
    return cachedRunes;
  } catch (e) {
    console.warn('Failed to load runesReforged:', e);
    cachedRunes = {};
    return cachedRunes;
  }
}

// Déclenche le chargement au démarrage (fire-and-forget)
if (typeof window !== 'undefined') {
  getAllRunes().catch(() => {});
  getBootsIds().catch(() => {});
}

/**
 * Charge items.json et construit un Set des IDs d'items "Boots".
 * Utilise les tags DataDragon. Le tag "Boots" est stable et couvre :
 *   - Boots de base (1001)
 *   - Boots T2 (3006, 3009, 3020, 3047, 3111, 3158)
 *   - Boots T3 (Swiftmarch 3170, Crimson Lucidity, Gunmetal Greaves, etc.)
 *   - Nouvelles bottes de futures saisons (automatique)
 */
export async function getBootsIds() {
  if (cachedBootsIds) return cachedBootsIds;
  try {
    const version = await getDataDragonVersion();
    const res = await fetch(`${DDRAGON_BASE}/cdn/${version}/data/en_US/item.json`);
    const data = await res.json();
    const ids = new Set();
    Object.entries(data.data || {}).forEach(([id, item]) => {
      const tags = Array.isArray(item.tags) ? item.tags : [];
      if (tags.includes('Boots')) {
        ids.add(parseInt(id, 10));
      }
    });
    cachedBootsIds = ids;
    return cachedBootsIds;
  } catch (e) {
    console.warn('Failed to load items for boots detection:', e);
    // Fallback : liste hardcodée des boots connus (saisons 14-15)
    cachedBootsIds = new Set([
      1001, // Boots (basic)
      3006, // Berserker's Greaves
      3009, // Boots of Swiftness
      3020, // Sorcerer's Shoes
      3047, // Plated Steelcaps
      3111, // Mercury's Treads
      3117, // Mobility Boots
      3158, // Ionian Boots of Lucidity
      // Tier 3 upgrades saison 2025+
      3010, // Symbiotic Soles
      3170, // Swiftmarch
      3171, // Gunmetal Greaves (à vérifier)
      3172, // Crimson Lucidity (à vérifier)
      3173, // Spellslinger's Shoes (à vérifier)
      3174, // Chainlaced Crushers (à vérifier)
      3175, // Armored Advance (à vérifier)
      3176, // Forever Forward (à vérifier)
    ]);
    return cachedBootsIds;
  }
}

/**
 * Cherche dans tous les slots d'items la première paire de bottes.
 * Retourne l'ID de la boot trouvée, ou null sinon.
 *
 * v7.2.4 : couvre slot 0..9 pour gérer le nouveau Role Quest slot ADC (2026).
 * Synchrone : utilise le cache (chargé au démarrage). Si pas encore chargé,
 * retourne null (et au prochain re-render, la détection marchera).
 */
export function findBootsInBuild(items) {
  if (!Array.isArray(items) || !cachedBootsIds) return null;
  // On scanne tous les slots disponibles. Les bottes peuvent être :
  //   - slot 0..5 (build classique sans quête)
  //   - slot 7+ (Role Quest slot ADC après complétion de la quête en 2026)
  //   - Le slot 6 (trinket) est aussi à scanner au cas où
  for (const id of items) {
    if (id && cachedBootsIds.has(id)) return id;
  }
  return null;
}

/**
 * URL DataDragon pour l'icône d'une rune.
 * Utilise le cache runesReforged.json pour résoudre l'iconPath.
 * Fallback vers une URL Community Dragon raisonnable si pas en cache.
 *
 * @param {number|string} runeId - perkId Riot
 */
export function getRuneIconUrl(runeId) {
  if (!runeId) return null;
  const version = cachedVersion || FALLBACK_VERSION;

  // Cas 1 : la rune est dans le cache → on construit l'URL DDragon
  if (cachedRunes && cachedRunes[runeId]) {
    return `${DDRAGON_BASE}/cdn/img/${cachedRunes[runeId]}`;
  }

  // Cas 2 : fallback Community Dragon, URL pratique par ID
  // Cette URL marche pour les runes, pas pour les styles
  return `https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/v1/perks/${runeId}.png`;
}

/**
 * URL d'un style de rune (Précision, Domination, etc.). Style IDs : 8000, 8100...
 */
export function getRuneStyleIconUrl(styleId) {
  if (!styleId) return null;
  if (cachedRunes && cachedRunes[styleId]) {
    return `${DDRAGON_BASE}/cdn/img/${cachedRunes[styleId]}`;
  }
  return `https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/styles/${styleId}.png`;
}

// Helper pour retrouver l'ID Data Dragon à partir d'un nom display
export function findChampionId(name) {
  if (!cachedChampions) return name;
  const found = cachedChampions.find(c => c.name === name || c.id === name);
  return found ? found.id : name;
}
